import ENDPOINTS from "./endpoint";
import apiInstance from "./apiService";

import {
  HomePhoneActivationPayload,
  HomePhoneFeature,
  HomePhoneFeatureRaw,
  HomePhoneOrder,
  HomePhoneOrderPayload,
  HomePhoneOrderRaw,
  HomePhoneProduct,
  HomePhoneProductRaw,
} from "../types/homePhone";

const firstDefined = <T>(
  ...values: Array<T | undefined | null | "">
): T | undefined =>
  values.find(
    (value): value is T =>
      value !== undefined && value !== null && value !== "",
  );

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => String(item).trim()).filter(Boolean);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const unwrapResponseData = <T>(payload: {
  data?: {
    data?: T;
  };
}): T => {
  return payload?.data?.data ?? (payload?.data as T);
};

const unwrapList = <T>(payload: {
  data?: {
    data?: T[] | { items?: T[] };
  };
}): T[] => {
  const data = unwrapResponseData<T[] | { items?: T[] }>(payload);

  if (Array.isArray(data)) {
    return data;
  }

  return data?.items ?? [];
};

const normalizeFeatureLabel = (feature: string | HomePhoneFeatureRaw) => {
  if (typeof feature === "string") {
    return feature.trim();
  }

  if (!isRecord(feature)) {
    return "";
  }

  const label = feature.label ?? feature.title ?? feature.name ?? feature.value;

  return typeof label === "string" ? label.trim() : "";
};

const normalizeFeatures = (
  features?: Array<string | HomePhoneFeatureRaw>,
): HomePhoneFeature[] => {
  if (!Array.isArray(features)) {
    return [];
  }

  return features
    .map((feature) => normalizeFeatureLabel(feature))
    .filter(Boolean)
    .map((label) => ({ label }));
};

const normalizeMacAddress = (macAddress: string): string => {
  return macAddress.trim().toUpperCase();
};

export const normalizeHomePhoneProduct = (
  product: HomePhoneProductRaw,
): HomePhoneProduct => {
  const pricing = product.pricing ?? {};
  const inventory = product.inventory ?? {};

  const images = asStringArray(product.images);

  return {
    id: String(firstDefined(product._id, product.id, product.productId) ?? ""),

    name: String(
      firstDefined(
        product.productName,
        product.deviceName,
        product.name,
        product.title,
      ) ?? "Home Phone Device",
    ),

    description: String(
      firstDefined(
        product.shortDescription,
        product.description,
        product.subtitle,
      ) ?? "",
    ),

    fullDescription: String(
      firstDefined(
        product.fullDescription,
        product.description,
        product.shortDescription,
      ) ?? "",
    ),

    productType: String(product.productType ?? ""),
    brand: String(product.brand ?? ""),
    modelNumber: String(product.modelNumber ?? ""),

    price: asNumber(pricing.price),
    currency: String(pricing.currency ?? "USD"),

    shippingCharge: asNumber(pricing.shippingCharge),
    activationCharge: asNumber(pricing.activationCharge),
    totalAmount: asNumber(pricing.totalAmount),

    inStock: Boolean(inventory.inStock),
    stock: asNumber(inventory.stock),

    imageUrl:
      firstDefined(
        product.imageUrl,
        product.image,
        product.thumbnail,
        product.productImage,
        images[0],
      ) ?? undefined,

    images,

    features: normalizeFeatures(
      firstDefined(product.features, product.highlights, product.specifications),
    ),

    raw: product as Record<string, unknown>,
  };
};

const normalizeOrder = (order: HomePhoneOrderRaw): HomePhoneOrder => {
  return {
    id: String(firstDefined(order._id, order.id, order.orderId) ?? ""),

    productId: String(order.productId ?? ""),
    productName: String(order.productName ?? ""),

    status: String(firstDefined(order.status, order.orderStatus) ?? ""),

    paymentStatus: String(order.paymentStatus ?? ""),
    activationStatus: String(order.activationStatus ?? ""),

    amount: asNumber(order.amount),
    currency: String(order.currency ?? "USD"),

    macAddress: order.macAddress ?? undefined,

    canActivate: Boolean(order.canActivate),
    canCompletePayment: Boolean(order.canCompletePayment),

    createdAt: order.createdAt,
    updatedAt: order.updatedAt,

    raw: order as Record<string, unknown>,
  };
};

export const homePhoneService = {
  async getProducts(): Promise<HomePhoneProduct[]> {
    const response = await apiInstance.get(ENDPOINTS.HOME_PHONE_PRODUCTS);

    return unwrapList<HomePhoneProductRaw>(response)
      .map(normalizeHomePhoneProduct)
      .filter((product) => Boolean(product.id));
  },

  async getProductDetails(productId: string): Promise<HomePhoneProduct> {
    const response = await apiInstance.get(
      `${ENDPOINTS.HOME_PHONE_PRODUCT_DETAILS}/${productId}`,
    );

    return normalizeHomePhoneProduct(
      unwrapResponseData<HomePhoneProductRaw>(response),
    );
  },

  async createOrder(
    productId: string,
    payload: HomePhoneOrderPayload,
  ): Promise<HomePhoneOrder> {
    const response = await apiInstance.post(
      `${ENDPOINTS.HOME_PHONE_ORDER}/${productId}/order`,
      payload,
    );

    return normalizeOrder(unwrapResponseData<HomePhoneOrderRaw>(response));
  },

  async activateOrder(
    orderId: string,
    payload: HomePhoneActivationPayload,
  ): Promise<HomePhoneOrder> {
    const response = await apiInstance.post(
      `${ENDPOINTS.HOME_PHONE_ORDER_ACTIVATION}/${orderId}/activate`,
      {
        ...payload,
        macAddress: normalizeMacAddress(payload.macAddress),
      },
    );

    return normalizeOrder(unwrapResponseData<HomePhoneOrderRaw>(response));
  },

  async getMyOrders(): Promise<HomePhoneOrder[]> {
    const response = await apiInstance.get(ENDPOINTS.HOME_PHONE_MY_ORDERS);

    return unwrapList<HomePhoneOrderRaw>(response)
      .map(normalizeOrder)
      .filter((order) => Boolean(order.id));
  },
};
